import {
  LoadingSpinner,
  Button,
  Listbox,
  InfoModal,
  Form,
  OverlayLoader,
} from "@talladega/components";
import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import { useRouter } from "next/router";
import {
  PROTECTION_RIGHTRIDE_CA_EMAIL,
  CONDITIONALLY_SUCCESSFUL_CHROME_DECODE,
} from "@common/constants";
import useSourceInfoFromQuery from "@modules/query-string/hooks/useSourceInfoFromQuery";
import { Trans, useTranslation } from "next-i18next";
import useFeatureFlags from "@modules/feature-flags/hooks/useFeatureFlags";
import { FeatureFlags } from "@modules/feature-flags/types/FeatureFlag";
import { useCallback, useEffect, useState } from "react";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { ArrowLeftIcon, ArrowRightIcon } from "@talladega/icons";
import {
  customerValidation,
  onSubmitError,
  gtmPushData,
} from "@talladega/helpers";
import Link from "next/link";
import { useNullableNumberMask } from "@common/hooks/useMask";
import { usePhoneNumberMask } from "@talladega/hooks";
import useCreateAftermarketLeadMutation, {
  decodeErrorResponse,
} from "@modules/purchase/warranty/hooks/useCreateAftermarketLeadMutation";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import useUser from "@modules/user/hooks/useUser";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import useUtmParamsFromQuery from "@modules/query-string/hooks/useUtmParamsFromQuery";
import { quoteMethods, QuoteMethodEnum } from "@common/enums/QuoteMethod";
import DealSegment from "@common/enums/DealSegment";
import { WarrantySegmentationFormProps } from "../types/WarrantySegmentationFormProps";
import {
  WarrantySegmentationMode,
  quoteVehicleFormMode,
} from "../types/WarrantySegmentationMode";
import NoCoveragesModal from "./coverage/NoCoveragesModal";
import getPromoCodeIsValid from "../api/getPromoCodeIsValid";

const warrantyQuoteFormFocusOrder = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "consent",
];

type Props = {
  onChangeMode(newMode: WarrantySegmentationMode): void;
  vehicleSearchMode: "ymm" | "vin";
};

const WarrantyQuotePersonalInfoForm = ({
  onChangeMode,
  vehicleSearchMode,
}: Props) => {
  const { isEnabled } = useFeatureFlags();
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const { locale: rlocale, query } = router;
  const locale = rlocale || "en";
  const { user } = useUser();

  const [isLeadFormSubmitting, setIsLeadFormSubmitting] = useState(false);
  const [isQuoteConfirmationModalOpen, setIsQuoteConfirmationModalOpen] =
    useState(false);

  const aftermarketPurchaseFromQuery = useAftermarketPurchaseFromQuery();
  const { source: querySource } = useSourceInfoFromQuery();

  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  // Aftermarket quote alert should show for all users except logged in users and users who have already started a purchase
  const aftermarketQuoteMethodEnabled =
    !user?.isLoggedIn &&
    isEnabled(FeatureFlags.AftermarketQuoteMethod) &&
    !purchase?.currentStepId;

  const {
    coverImage,
    sourceAdId,
    sourceUserId,
    sourceSessionId,
    sourceAppId,
    sourceLocationId,
    sourceCtaId,
  } = aftermarketPurchaseFromQuery;

  const { unMaskPipe: phoneUnMaskPipe } = usePhoneNumberMask();
  const { maskPipe: numberMaskPipe, unMaskPipe: numberUnMaskPipe } =
    useNullableNumberMask();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    getValues,
    setError,
  } = useFormContext<WarrantySegmentationFormProps>();

  // hack: on locale change, re-run the number mask
  useEffect(() => {
    const v = getValues("odometer");
    if (v != null) {
      setValue("odometer", numberMaskPipe(v.replace(/[^\d.]/, "")));
    }
  }, [getValues, numberMaskPipe, router.locale, setValue]);

  const createAftermarketLeadMutation = useCreateAftermarketLeadMutation();

  const utmParams = useUtmParamsFromQuery();

  const [showOverlay, setShowOverlay] = useState(false);

  const [showNoCoveragesModal, setShowNoCoveragesModal] = useState(false);

  const sendLeadForm = useCallback(
    (formInput: WarrantySegmentationFormProps, promoCodeId?: string | null) => {
      const {
        firstName: formFirstName,
        lastName: formLastName,
        phone: formPhone,
        email: formEmail,
        province,
        odometer,
        vin,
        yearMakeModel,
        consent,
        quoteMethod,
      } = formInput;

      const isVinSearchMode = vehicleSearchMode === "vin";

      if (!province || !odometer) {
        return;
      }

      setIsLeadFormSubmitting(true);
      createAftermarketLeadMutation.mutate(
        {
          firstName: formFirstName,
          lastName: formLastName,
          phone: phoneUnMaskPipe(formPhone),
          email: formEmail,
          province: province.abbr,
          odometer: numberUnMaskPipe(odometer) ?? 0,
          source: querySource,
          vin: vin ?? null,
          year: vin && isVinSearchMode ? null : yearMakeModel?.year ?? null,
          make: vin && isVinSearchMode ? null : yearMakeModel?.make ?? null,
          model: vin && isVinSearchMode ? null : yearMakeModel?.model ?? null,
          trim: vin && isVinSearchMode ? null : yearMakeModel?.trim ?? null,
          promoCodeId: promoCodeId ?? null,
          isConsentGiven: consent,
          sourceAdId,
          sourceUserId,
          sourceSessionId,
          sourceAppId,
          sourceLocationId,
          sourceCtaId,
          vehicleCoverImage: coverImage,
          quoteMethod: aftermarketQuoteMethodEnabled ? quoteMethod.id : null,
          dealSegment: DealSegment.InstantQuote,
          fuelType: vin ? null : formInput.fuelType?.name ?? null,
          ...utmParams,
          locale,
        },
        {
          onSuccess: (data) => {
            gtmPushData({
              event: "AftermarketPurchase",
              element: "Landing Page",
              description: "Free Instant Quote",
              user_id: user?.id,
            });

            hotjar.event(
              HotjarEvent.AftermarketConvertPurchaseFreeInstantQuote
            );

            if (
              data &&
              data.success === false &&
              data.message === decodeErrorResponse.invalidVin
            ) {
              setError("vin", {
                message: t("common:invalid_vin_please_try_again"),
              });

              setShowOverlay(false);

              if (aftermarketQuoteMethodEnabled) {
                return;
              }
            }

            if (data && data.success === false) {
              setShowNoCoveragesModal(true);

              if (aftermarketQuoteMethodEnabled) {
                return;
              }
            }

            const ratesError =
              data &&
              data.success === false &&
              data.message === decodeErrorResponse.ratesFailed;

            if (aftermarketQuoteMethodEnabled && !ratesError) {
              setIsQuoteConfirmationModalOpen(true);
            } else {
              // if the rates check failed, send to the quote page anyway
              let decodedVin: string | undefined = "";
              if (
                data &&
                data.decodedVin &&
                data.success === true &&
                data.message === CONDITIONALLY_SUCCESSFUL_CHROME_DECODE
              ) {
                decodedVin = data.decodedVin;
              }

              // we want to keep the query params as they were passed to us so the next
              // page behaves as it always has (except loginSuccessful)
              const { loginSuccessful, ...restOfQuery } = query;
              router.push({
                pathname: "warranty/quote",
                query: {
                  ...restOfQuery,
                  vin:
                    vin && isVinSearchMode
                      ? decodedVin || vin || undefined
                      : undefined,
                  // Exclude YMMT & fuelType if vin is used
                  year:
                    vin && isVinSearchMode ? undefined : yearMakeModel?.year,
                  make:
                    vin && isVinSearchMode ? undefined : yearMakeModel?.make,
                  model:
                    vin && isVinSearchMode ? undefined : yearMakeModel?.model,
                  trim:
                    vin && isVinSearchMode ? undefined : yearMakeModel?.trim,
                  fuelType: vin ? undefined : formInput.fuelType?.name,
                  province: province.abbr,
                  first: formFirstName,
                  last: formLastName,
                  email: formEmail,
                  phoneNumber: phoneUnMaskPipe(formPhone),
                  odometer: numberUnMaskPipe(odometer) ?? 0,
                },
              });
            }
          },
          onError: () => {
            setShowOverlay(false);
          },
          onSettled: () => {
            setIsLeadFormSubmitting(false);
          },
        }
      );
    },
    [
      aftermarketQuoteMethodEnabled,
      coverImage,
      createAftermarketLeadMutation,
      locale,
      numberUnMaskPipe,
      phoneUnMaskPipe,
      query,
      querySource,
      router,
      setError,
      sourceAdId,
      sourceAppId,
      sourceCtaId,
      sourceLocationId,
      sourceSessionId,
      sourceUserId,
      t,
      user?.id,
      utmParams,
      vehicleSearchMode,
    ]
  );

  const handleQuoteConfirmationModalClose = () => {
    setIsQuoteConfirmationModalOpen(false);
    onChangeMode(quoteVehicleFormMode);
  };

  const handleFormSubmit: SubmitHandler<WarrantySegmentationFormProps> = (
    formInput: WarrantySegmentationFormProps
  ) => {
    if (formInput.promoCode) {
      setShowOverlay(true);
      getPromoCodeIsValid(formInput.promoCode).then((res) => {
        if (res.error) {
          if (res.error === "Promo Code cannot be found") {
            setError("promoCode", {
              message: t("purchase:promo_code_cannot_be_found"),
            });
          } else if (res.error === "Promo Code has expired") {
            setError("promoCode", {
              message: t("purchase:promo_code_has_expired"),
            });
          }
          // Switch view back to where error occurred.
          onChangeMode(quoteVehicleFormMode);
          setShowOverlay(false);
        } else {
          sendLeadForm(formInput, res.id);
        }
      });
    } else {
      sendLeadForm(formInput);
    }
  };

  const onClickBackButton = () => {
    onChangeMode(quoteVehicleFormMode);
  };

  return (
    <OverlayLoader isLoading={showOverlay}>
      <form
        onSubmit={handleSubmit(
          handleFormSubmit,
          onSubmitError(warrantyQuoteFormFocusOrder)
        )}
      >
        <div className="grid grid-cols-1 gap-3">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 font-bold text-lg">
              {t("common:about_you")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Form.FirstName
                fieldError={errors.firstName}
                {...register("firstName", customerValidation.firstName)}
              />
              <Form.LastName
                fieldError={errors.lastName}
                {...register("lastName", customerValidation.lastName)}
              />
            </div>

            <Form.Email
              fieldError={errors.email}
              {...register("email", customerValidation.email)}
            />
            <Form.Phone
              fieldError={errors.phone}
              {...register("phone", customerValidation.phoneNumber)}
            />
            {aftermarketQuoteMethodEnabled && (
              <Controller
                name="quoteMethod"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("purchase:quote_type_required"),
                  },
                }}
                defaultValue={quoteMethods[0]}
                render={({ field: { onChange, ref } }) => (
                  <Listbox
                    label={t("purchase:send_me_my_quote_via")}
                    onChange={onChange}
                    selectedItem={watch("quoteMethod")}
                    items={quoteMethods}
                    displayOption={(item) => t(item?.name) ?? ""}
                    displayButton={(item) =>
                      item?.name != null
                        ? t(item.name)
                        : t(quoteMethods[0].name)
                    }
                    keyOption={(p) => p.id}
                    valueOption={(p) => p}
                    buttonRef={ref}
                    status={errors.quoteMethod ? "error" : "default"}
                    feedback={errors.quoteMethod?.message}
                  />
                )}
              />
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-4">
          <Form.Checkbox
            className="text-sm font-semibold"
            label={
              <Trans
                t={t}
                i18nKey="purchase:customer_details_terms_and_conditions"
                components={{
                  a: (
                    <Link
                      className="text-primary-500 underline"
                      href="/privacy-policy"
                      target="_blank"
                    />
                  ),
                  b: (
                    <Link
                      className="text-primary-500 underline"
                      href="/terms-conditions"
                      target="_blank"
                    />
                  ),
                }}
              />
            }
            status={errors.consent ? "error" : "default"}
            feedback={errors.consent?.message}
            {...register("consent", {
              required: {
                value: true,
                message: t("common:form_error_required_consent"),
              },
            })}
          />
          <div className="mt-6 flex flex-col gap-4 w-full">
            <div className="flex flex-col items-stretch space-y-4 w-full">
              <div className="hidden text-center sm:block">
                <Button
                  type="submit"
                  className="w-full"
                  size="extra-large"
                  rightIcon={<ArrowRightIcon />}
                >
                  {t("purchase:get_my_quote")}
                </Button>
              </div>
              <div className="block text-center sm:hidden">
                <Button
                  type="submit"
                  className="w-full"
                  rightIcon={<ArrowRightIcon />}
                >
                  {t("purchase:get_my_quote")}
                </Button>
              </div>
              <Button
                fill="link"
                buttonStyle="borderless"
                spacing="tight-hug"
                leftIcon={<ArrowLeftIcon />}
                className="text-primary-500 underline hover:no-underline"
                onClick={() => onClickBackButton()}
              >
                {t("common:go_back")}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {aftermarketQuoteMethodEnabled && (
        <InfoModal
          title={t("purchase:we_are_on_it")}
          isOpen={isQuoteConfirmationModalOpen}
          onClose={handleQuoteConfirmationModalClose}
          primaryButtonText={t("common:done")}
        >
          <div className="text-primary">
            <h3>{t("purchase:your_quote_is_being_generated_and_sent")}</h3>
            {watch("quoteMethod")?.id === QuoteMethodEnum.Email ? (
              <div className="text-md mt-4 font-semibold">
                <Trans
                  t={t}
                  i18nKey="purchase:please_check_your_email_and_junk_folder"
                  values={{
                    email: watch("email"),
                  }}
                  components={{
                    bold: <span className="font-semibold" />,
                  }}
                />
              </div>
            ) : (
              <div className="text-md mt-4 font-semibold">
                <Trans
                  t={t}
                  i18nKey="purchase:please_check_your_phone"
                  values={{
                    phone: watch("phone"),
                  }}
                  components={{
                    bold: <span className="font-semibold" />,
                  }}
                />
              </div>
            )}
            <div className="mt-4 text-sm font-normal">
              <Trans
                t={t}
                i18nKey="purchase:if_you_have_not_received_your_quote"
                values={{ email: PROTECTION_RIGHTRIDE_CA_EMAIL }}
                components={{
                  a: (
                    <Link
                      className="text-primary-500 underline"
                      href={`mailto:${PROTECTION_RIGHTRIDE_CA_EMAIL}`}
                    />
                  ),
                }}
              />
            </div>
          </div>
        </InfoModal>
      )}
      {showNoCoveragesModal && (
        <NoCoveragesModal
          showNoCoveragesModal={showNoCoveragesModal}
          setShowNoCoveragesModal={setShowNoCoveragesModal}
          onPrimaryClick={() => setShowNoCoveragesModal(false)}
          primaryButtonText={t("common:okay")}
          modalMode="Generic"
          userId={user?.id}
        />
      )}
      <LoadingSpinner
        isLoading={isLeadFormSubmitting}
        isOverlay
        iconColor="primary-dark"
      />
    </OverlayLoader>
  );
};

export default WarrantyQuotePersonalInfoForm;
